import { ReactNode, useState, useCallback, useRef } from "react";
import { i18nLanguage } from "i18n";
import {
  TripperData,
  SegmentData,
  SegmentBlock,
  StopBlock,
  TweenBlock,
  GroupBlock,
  OperatorRecord,
} from "../types";
import { DistanceUnit } from "shared/types";
import { useTripperScrollContext, useAtlasMode, useTripperWidgetContext } from "../context/TripperWidgetContext";
import { useTripperTrack } from "../context/TripperTrackingContext";
import { TripperHeader } from "./content/TripperHeader";
import { TripperSegmentHeader } from "./content/TripperSegmentHeader";
import { TripperStopCard } from "./content/TripperStopCard";
import { TripperTween } from "./content/TripperTween";
import { TripperPlaceGroup } from "./content/TripperPlaceGroup";
import { TripperDistanceTween } from "./content/TripperDistanceTween";
import { TripperFooter } from "./content/TripperFooter";
import { getLocalizedString } from "../utils/getLocalizedString";
import {
  isShowingAllBusinessTypes,
  isStopVisibleForBusinessTypeFilter,
} from "../utils/businessTypeFilters";
import { flattenStopBlocks } from "../utils/mapHelpers";

type TripperContentProps = {
  data: TripperData;
  language: i18nLanguage;
  onStopNameClick?: (stop: StopBlock, operator: OperatorRecord, markerNumber: number) => void;
  onOutboundClick?: (operatorEntityId: string, linkType: "google_maps" | "website") => void;
  unit?: DistanceUnit;
  /** Use divs instead of sections — avoids Chromium print treating <section> as hard page breaks. */
  forPdf?: boolean;
};

/** Count all stop blocks across all segments (including inside groups). */
function countStops(segments: SegmentData[]): number {
  let count = 0;
  for (const segment of segments) {
    for (const block of segment.items) {
      if (block.blockType === "STOP") count++;
      else if (block.blockType === "GROUP") {
        for (const child of (block as GroupBlock).items) {
          if (child.blockType === "STOP") count++;
        }
      }
    }
  }
  return count;
}

const toRad = (deg: number) => (deg * Math.PI) / 180;

const haversineMeters = (a: [number, number], b: [number, number]): number => {
  const R = 6371000;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const getStopCoordinates = (
  stop: StopBlock,
  operatorByEntityId: Record<string, OperatorRecord>,
): [number, number] | null => {
  const entityId = stop.entityRef?.entityId?.toString();
  if (!entityId) return null;
  const coordinates = operatorByEntityId[entityId]?.contact?.address?.location?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length !== 2) return null;
  const [lng, lat] = coordinates;
  if (typeof lng !== "number" || typeof lat !== "number") return null;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return [lng, lat];
};

const estimateTravelSeconds = (
  meters: number,
  travelType: "DRIVING" | "CYCLING" | "WALKING" | undefined,
): number => {
  // Approx speeds used only when route legs are missing.
  const metersPerSecond =
    travelType === "WALKING"
      ? 1.25
      : travelType === "CYCLING"
        ? 4.5
        : 11.0;
  return Math.round(meters / metersPerSecond);
};

const getFirstStopFromBlock = (block: SegmentBlock): StopBlock | null => {
  if (block.blockType === "STOP") return block as StopBlock;
  if (block.blockType === "GROUP") {
    const firstGroupStop = (block as GroupBlock).items.find((item) => item.blockType === "STOP");
    return (firstGroupStop as StopBlock | undefined) ?? null;
  }
  return null;
};

export const TripperContent = ({
  data,
  language,
  onStopNameClick,
  onOutboundClick,
  unit = "metric",
  forPdf = false,
}: TripperContentProps) => {
  const { activeItemId, activeSegmentId, activeStopGroupId } =
    useTripperScrollContext();
  const atlasMode = useAtlasMode();
  const { businessTypeFilterSelection, businessTypeFiltersEnabled } = useTripperWidgetContext();
  const trackPostHogEvent = useTripperTrack();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const hasTrackedPdfDownload = useRef(false);

  const handleDownloadPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const { generatePdf } = await import("../pdf/generatePdf");
      await generatePdf({ data, language });
      if (!hasTrackedPdfDownload.current) {
        hasTrackedPdfDownload.current = true;
        trackPostHogEvent("pdf_download");
      }
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [data, language, trackPostHogEvent]);

  if (!data.itinerary) return null;

  const isStopVisible = (stop: StopBlock) =>
    !businessTypeFiltersEnabled
    || isStopVisibleForBusinessTypeFilter(
      stop,
      data.operatorByEntityId,
      businessTypeFilterSelection,
    );

  const filtersActive =
    businessTypeFiltersEnabled && !isShowingAllBusinessTypes(businessTypeFilterSelection);
  const visibleStopCount = flattenStopBlocks(data.segments).filter(isStopVisible).length;

  let markerNumber = 0;
  const visibleSegments = data.segments.filter((s) => s.items.length > 0);
  const isSingleSegment = visibleSegments.length <= 1;

  const renderStopCard = (stop: StopBlock, isActive: boolean, nextStop?: StopBlock | null) => {
    if (!isStopVisible(stop)) return null;
    markerNumber += 1;
    return (
      <div key={stop._id}>
        <TripperStopCard
          item={stop}
          operatorByEntityId={data.operatorByEntityId}
          language={language}
          markerNumber={markerNumber}
          isActive={isActive}
          onNameClick={onStopNameClick}
          onOutboundClick={onOutboundClick}
        />
        {nextStop && (
          <div style={{ margin: "32px 0" }}>
            {!atlasMode && (() => {
              const routeDistance = nextStop?.distanceFromPrev ?? 0;
              const routeTime = nextStop?.timeFromPrev ?? 0;
              if (routeDistance > 0 || routeTime > 0) {
                return (
            <TripperDistanceTween
              distanceFromPrev={routeDistance}
              timeFromPrev={routeTime}
              travelType={data.itinerary?.travelType}
              unit={unit}
            />
                );
              }
              const from = getStopCoordinates(stop, data.operatorByEntityId);
              const to = getStopCoordinates(nextStop, data.operatorByEntityId);
              if (!from || !to) return null;
              const fallbackMeters = haversineMeters(from, to);
              if (!(fallbackMeters > 0)) return null;
              return (
                <TripperDistanceTween
                  distanceFromPrev={fallbackMeters}
                  timeFromPrev={estimateTravelSeconds(fallbackMeters, data.itinerary?.travelType)}
                  travelType={data.itinerary?.travelType}
                  unit={unit}
                />
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <TripperHeader
        itinerary={data.itinerary}
        language={language}
        segmentCount={data.segments.filter((s) => s.items.length > 0).length}
        stopCount={countStops(data.segments)}
        isDayTrip={data.itinerary.tripperSettings?.segmentPreset === "DAY_TRIP"}
        unit={unit}
        onDownloadPdf={handleDownloadPdf}
        isGeneratingPdf={isGeneratingPdf}
      />

      <div className="tripper-sections">
        {filtersActive && visibleStopCount === 0 && (
          <div className="tripper-filter-empty" role="status">
            No stops match the selected business types.
          </div>
        )}
        {visibleSegments.map((segment, segmentIndex) => {
          const items = segment.items;
          const renderedItems: ReactNode[] = [];
          const findNextStopAfterBlock = (
            fromSegmentIndex: number,
            fromBlockIndex: number,
          ): StopBlock | null => {
            const currentItems = visibleSegments[fromSegmentIndex]?.items ?? [];
            for (let nextIndex = fromBlockIndex + 1; nextIndex < currentItems.length; nextIndex += 1) {
              const firstStop = getFirstStopFromBlock(currentItems[nextIndex]);
              if (firstStop && isStopVisible(firstStop)) return firstStop;
            }
            for (let nextSegmentIdx = fromSegmentIndex + 1; nextSegmentIdx < visibleSegments.length; nextSegmentIdx += 1) {
              for (const block of visibleSegments[nextSegmentIdx].items) {
                const firstStop = getFirstStopFromBlock(block);
                if (firstStop && isStopVisible(firstStop)) return firstStop;
              }
            }
            return null;
          };

          const segmentDistance = items.reduce((sum, block) => {
            if (block.blockType === "STOP") return sum + ((block as StopBlock).distanceFromPrev ?? 0);
            if (block.blockType === "GROUP") {
              return sum + (block as GroupBlock).items.reduce(
                (gs, child) => gs + (child.blockType === "STOP" ? ((child as StopBlock).distanceFromPrev ?? 0) : 0),
                0,
              );
            }
            return sum;
          }, 0);
          const segmentDistanceMeters = segmentDistance > 0 ? segmentDistance : null;

          for (let index = 0; index < items.length; index += 1) {
            const block = items[index];

            if (block.blockType === "GROUP") {
              const group = block as GroupBlock;
              const hasVisibleStops = group.items.some(
                item => item.blockType === "STOP" && isStopVisible(item as StopBlock),
              );
              if (!hasVisibleStops) continue;

              const groupLastStop = [...group.items]
                .reverse()
                .find((item) => item.blockType === "STOP" && isStopVisible(item as StopBlock)) as
                | StopBlock
                | undefined;
              const nextStopAfterGroup = findNextStopAfterBlock(segmentIndex, index);

              const groupFooterDistance = (() => {
                if (!groupLastStop || !nextStopAfterGroup) return null;
                const routeDistance = nextStopAfterGroup.distanceFromPrev ?? 0;
                const routeTime = nextStopAfterGroup.timeFromPrev ?? 0;
                const tween = (() => {
                  if (routeDistance > 0 || routeTime > 0) {
                    return (
                      <TripperDistanceTween
                        distanceFromPrev={routeDistance}
                        timeFromPrev={routeTime}
                        travelType={data.itinerary?.travelType}
                        unit={unit}
                        showTrailingLine={false}
                      />
                    );
                  }
                  const from = getStopCoordinates(groupLastStop, data.operatorByEntityId);
                  const to = getStopCoordinates(nextStopAfterGroup, data.operatorByEntityId);
                  if (!from || !to) return null;
                  const fallbackMeters = haversineMeters(from, to);
                  if (!(fallbackMeters > 0)) return null;
                  return (
                    <TripperDistanceTween
                      distanceFromPrev={fallbackMeters}
                      timeFromPrev={estimateTravelSeconds(fallbackMeters, data.itinerary?.travelType)}
                      travelType={data.itinerary?.travelType}
                      unit={unit}
                      showTrailingLine={false}
                    />
                  );
                })();
                if (atlasMode) {
                  return <div style={{ margin: "32px 0 0" }} aria-hidden />;
                }
                return tween;
              })();

              renderedItems.push(
                <TripperPlaceGroup
                  key={`group-${group._id}`}
                  group={group}
                  language={language}
                  isActive={activeStopGroupId === group._id}
                  footerDistance={groupFooterDistance}
                >
                  {group.items.map((child, childIndex) => {
                    const isOptionsGroup = group.groupType === "OPTIONS";
                    const allActiveInGroup = isOptionsGroup && activeStopGroupId === group._id;
                    if (child.blockType === "STOP") {
                      const stop = child as StopBlock;
                      if (!isStopVisible(stop)) return null;
                      markerNumber += 1;
                      return (
                        <div key={stop._id}>
                          {childIndex > 0 && !isOptionsGroup && (
                            <div style={{ marginBottom: 32 }}>
                              {!atlasMode && (() => {
                                const routeDistance = stop.distanceFromPrev ?? 0;
                                const routeTime = stop.timeFromPrev ?? 0;
                                if (routeDistance > 0 || routeTime > 0) {
                                  return (
                              <TripperDistanceTween
                                distanceFromPrev={routeDistance}
                                timeFromPrev={routeTime}
                                travelType={data.itinerary?.travelType}
                                unit={unit}
                              />
                                  );
                                }
                                const prev = group.items[childIndex - 1];
                                if (!prev || prev.blockType !== "STOP") return null;
                                const from = getStopCoordinates(prev as StopBlock, data.operatorByEntityId);
                                const to = getStopCoordinates(stop, data.operatorByEntityId);
                                if (!from || !to) return null;
                                const fallbackMeters = haversineMeters(from, to);
                                if (!(fallbackMeters > 0)) return null;
                                return (
                                  <TripperDistanceTween
                                    distanceFromPrev={fallbackMeters}
                                    timeFromPrev={estimateTravelSeconds(fallbackMeters, data.itinerary?.travelType)}
                                    travelType={data.itinerary?.travelType}
                                    unit={unit}
                                  />
                                );
                              })()}
                            </div>
                          )}
                          <TripperStopCard
                            item={stop}
                            operatorByEntityId={data.operatorByEntityId}
                            language={language}
                            markerNumber={markerNumber}
                            isActive={allActiveInGroup || activeItemId === stop._id}
                            hideTag={isOptionsGroup}
                            onNameClick={onStopNameClick}
                            onOutboundClick={onOutboundClick}
                          />
                        </div>
                      );
                    }
                    return (
                      <TripperTween
                        key={child._id}
                        item={child as TweenBlock}
                        language={language}
                        isActive={allActiveInGroup || activeItemId === child._id}
                      />
                    );
                  })}
                </TripperPlaceGroup>,
              );
            } else if (block.blockType === "STOP") {
              const stop = block as StopBlock;
              if (!isStopVisible(stop)) continue;
              const nextStop = findNextStopAfterBlock(segmentIndex, index);
              const prevBlock = index > 0 ? items[index - 1] : null;
              const topLevelStopSpacing = (index === 0 || (prevBlock && prevBlock.blockType !== "STOP"))
                ? "var(--wa-spacing-500, 32px)"
                : undefined;
              renderedItems.push(
                <div key={`top-stop-${stop._id}`} style={{ marginTop: topLevelStopSpacing }}>
                  {renderStopCard(stop, activeItemId === stop._id, nextStop)}
                </div>,
              );
            } else {
              const tween = block as TweenBlock;
              renderedItems.push(
                <TripperTween
                  key={tween._id}
                  item={tween}
                  language={language}
                  isActive={activeItemId === tween._id}
                />,
              );
            }
          }

          if (renderedItems.length === 0) return null;

          const segmentLabel =
            getLocalizedString(segment.label ?? undefined, language) || "Segment";
          const Section = forPdf ? "div" : "section";
          const sectionProps = forPdf
            ? { role: "region" as const, "aria-label": segmentLabel }
            : {};

          return (
            <Section
              key={segment._id}
              className="tripper-section"
              data-segment-id={segment._id}
              {...sectionProps}
            >
              {!isSingleSegment && !atlasMode && (
                <TripperSegmentHeader
                  segment={segment}
                  language={language}
                  isActive={activeSegmentId === segment._id}
                  distanceMeters={segmentDistanceMeters}
                  isMultiDay={data.segments.length > 1}
                  dayNumber={segmentIndex + 1}
                  unit={unit}
                />
              )}
              <div className="tripper-section-items">{renderedItems}</div>
            </Section>
          );
        })}
      </div>

      <TripperFooter />
    </>
  );
};
